// User location for localizing web answers (like Perplexity / Google AI mode). Used to geo-bias
// web search and to ground AI answers in the user's place (local prices, stores, currency, weather).
// Resolution order — most accurate first:
//   1. Browser geolocation (one permission prompt, remembered) + client-side reverse geocode.
//   2. IP lookup (ipwho.is, then ipapi.co) — approximate, often wrong on VPN/mobile carriers.
// Cached for a day; v2 key so older (often wrong) v1 IP results are re-resolved.
//
// Before any of that runs there is `instantGuess()`: the country the browser already knows, read
// synchronously off the time zone (and the UI language as a fallback). It costs no network call and
// no permission prompt, so the very first search of a fresh session can carry a country even though
// the real resolution is still in flight — the race in research/start used to send nothing at all,
// which is how a user in Istanbul got euro prices (Pouria, 2026-08-21).

const CACHE_KEY = 'busy.geo.v2'
const TTL_MS = 24 * 60 * 60 * 1000 // refresh at most once a day

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || (Date.now() - (parsed.savedAt || 0)) > TTL_MS) return null
    return parsed.value || null
  } catch {
    return null
  }
}

function writeCache(value) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ savedAt: Date.now(), value }))
  } catch {
    /* storage unavailable — ignore, we just won't cache */
  }
}

// IANA time-zone → country, as the lowercase ENGLISH NAME the rest of this module produces
// (`countryName.toLowerCase()` from the reverse geocoder / IP providers) and that ai-srv's
// SearchRegion.CodeByName matches on. Names, not ISO codes: SearchRegion accepts both, but a name
// is also what SearchRegion.QueryLabel can title-case into a query word ("Turkey"), whereas a bare
// code stays "TR" in the merchant query — so names keep this path identical to the ipLookup path.
//
// The zone is the strongest location signal a browser gives away for free: it is the clock the
// device is actually set to. Countries with several zones list all of the common ones; zones with
// several countries are not in here at all (a wrong country is worse than none).
const COUNTRY_BY_ZONE = {
  // Europe
  'Europe/Istanbul': 'turkey', 'Asia/Istanbul': 'turkey',
  'Europe/Berlin': 'germany', 'Europe/Busingen': 'germany',
  'Europe/Vienna': 'austria', 'Europe/Zurich': 'switzerland',
  'Europe/Paris': 'france', 'Europe/Brussels': 'belgium',
  'Europe/Amsterdam': 'netherlands', 'Europe/Luxembourg': 'luxembourg',
  'Europe/Madrid': 'spain', 'Africa/Ceuta': 'spain', 'Atlantic/Canary': 'spain',
  'Europe/Lisbon': 'portugal', 'Atlantic/Azores': 'portugal', 'Atlantic/Madeira': 'portugal',
  'Europe/Rome': 'italy', 'Europe/San_Marino': 'san marino', 'Europe/Vatican': 'vatican city',
  'Europe/Malta': 'malta', 'Europe/Monaco': 'monaco', 'Europe/Andorra': 'andorra',
  'Europe/Gibraltar': 'gibraltar',
  'Europe/Warsaw': 'poland', 'Europe/Prague': 'czechia', 'Europe/Bratislava': 'slovakia',
  'Europe/Budapest': 'hungary', 'Europe/Bucharest': 'romania', 'Europe/Sofia': 'bulgaria',
  'Europe/Athens': 'greece', 'Europe/Nicosia': 'cyprus', 'Asia/Nicosia': 'cyprus',
  'Europe/Kyiv': 'ukraine', 'Europe/Kiev': 'ukraine', 'Europe/Uzhgorod': 'ukraine',
  'Europe/Zaporozhye': 'ukraine',
  'Europe/Minsk': 'belarus', 'Europe/Chisinau': 'moldova',
  'Europe/Tallinn': 'estonia', 'Europe/Riga': 'latvia', 'Europe/Vilnius': 'lithuania',
  'Europe/Belgrade': 'serbia', 'Europe/Zagreb': 'croatia', 'Europe/Ljubljana': 'slovenia',
  'Europe/Sarajevo': 'bosnia and herzegovina', 'Europe/Skopje': 'north macedonia',
  'Europe/Podgorica': 'montenegro', 'Europe/Tirane': 'albania',
  'Europe/Stockholm': 'sweden', 'Europe/Oslo': 'norway', 'Europe/Copenhagen': 'denmark',
  'Europe/Helsinki': 'finland', 'Europe/Reykjavik': 'iceland', 'Atlantic/Reykjavik': 'iceland',
  'Atlantic/Faroe': 'faroe islands',
  'Europe/London': 'united kingdom', 'Europe/Belfast': 'united kingdom',
  'Europe/Isle_of_Man': 'united kingdom', 'Europe/Jersey': 'united kingdom',
  'Europe/Guernsey': 'united kingdom', 'Europe/Dublin': 'ireland',
  'Europe/Moscow': 'russia', 'Europe/Kaliningrad': 'russia', 'Europe/Samara': 'russia',
  'Europe/Volgograd': 'russia', 'Europe/Saratov': 'russia', 'Europe/Astrakhan': 'russia',
  'Europe/Ulyanovsk': 'russia', 'Europe/Kirov': 'russia',
  // Americas — United States
  'America/New_York': 'united states', 'America/Chicago': 'united states',
  'America/Denver': 'united states', 'America/Los_Angeles': 'united states',
  'America/Phoenix': 'united states', 'America/Detroit': 'united states',
  'America/Boise': 'united states', 'America/Anchorage': 'united states',
  'America/Juneau': 'united states', 'America/Sitka': 'united states',
  'America/Nome': 'united states', 'America/Adak': 'united states',
  'America/Menominee': 'united states', 'America/Indiana/Indianapolis': 'united states',
  'America/Kentucky/Louisville': 'united states', 'America/North_Dakota/Center': 'united states',
  'Pacific/Honolulu': 'united states', 'America/Puerto_Rico': 'puerto rico',
  // Americas — Canada
  'America/Toronto': 'canada', 'America/Montreal': 'canada', 'America/Vancouver': 'canada',
  'America/Edmonton': 'canada', 'America/Winnipeg': 'canada', 'America/Halifax': 'canada',
  'America/Moncton': 'canada', 'America/St_Johns': 'canada', 'America/Regina': 'canada',
  'America/Whitehorse': 'canada', 'America/Yellowknife': 'canada', 'America/Iqaluit': 'canada',
  // Americas — Latin America
  'America/Mexico_City': 'mexico', 'America/Monterrey': 'mexico', 'America/Tijuana': 'mexico',
  'America/Cancun': 'mexico', 'America/Merida': 'mexico', 'America/Chihuahua': 'mexico',
  'America/Mazatlan': 'mexico', 'America/Hermosillo': 'mexico',
  'America/Sao_Paulo': 'brazil', 'America/Bahia': 'brazil', 'America/Fortaleza': 'brazil',
  'America/Recife': 'brazil', 'America/Manaus': 'brazil', 'America/Belem': 'brazil',
  'America/Cuiaba': 'brazil', 'America/Campo_Grande': 'brazil', 'America/Porto_Velho': 'brazil',
  'America/Rio_Branco': 'brazil', 'America/Maceio': 'brazil', 'America/Araguaina': 'brazil',
  'America/Santarem': 'brazil', 'America/Boa_Vista': 'brazil', 'America/Noronha': 'brazil',
  'America/Argentina/Buenos_Aires': 'argentina', 'America/Buenos_Aires': 'argentina',
  'America/Argentina/Cordoba': 'argentina', 'America/Argentina/Mendoza': 'argentina',
  'America/Argentina/Salta': 'argentina', 'America/Argentina/Tucuman': 'argentina',
  'America/Argentina/Jujuy': 'argentina', 'America/Argentina/Catamarca': 'argentina',
  'America/Argentina/La_Rioja': 'argentina', 'America/Argentina/San_Juan': 'argentina',
  'America/Argentina/San_Luis': 'argentina', 'America/Argentina/Rio_Gallegos': 'argentina',
  'America/Argentina/Ushuaia': 'argentina',
  'America/Santiago': 'chile', 'America/Punta_Arenas': 'chile',
  'America/Bogota': 'colombia', 'America/Lima': 'peru', 'America/Caracas': 'venezuela',
  'America/Guayaquil': 'ecuador', 'America/La_Paz': 'bolivia', 'America/Asuncion': 'paraguay',
  'America/Montevideo': 'uruguay', 'America/Guyana': 'guyana', 'America/Paramaribo': 'suriname',
  'America/Cayenne': 'french guiana',
  'America/Havana': 'cuba', 'America/Santo_Domingo': 'dominican republic',
  'America/Port-au-Prince': 'haiti', 'America/Jamaica': 'jamaica', 'America/Nassau': 'bahamas',
  'America/Barbados': 'barbados', 'America/Port_of_Spain': 'trinidad and tobago',
  'America/Panama': 'panama', 'America/Costa_Rica': 'costa rica',
  'America/Guatemala': 'guatemala', 'America/El_Salvador': 'el salvador',
  'America/Tegucigalpa': 'honduras', 'America/Managua': 'nicaragua', 'America/Belize': 'belize',
  // Asia
  'Asia/Tokyo': 'japan', 'Asia/Seoul': 'south korea', 'Asia/Pyongyang': 'north korea',
  'Asia/Shanghai': 'china', 'Asia/Chongqing': 'china', 'Asia/Harbin': 'china',
  'Asia/Urumqi': 'china', 'Asia/Hong_Kong': 'hong kong', 'Asia/Macau': 'macau',
  'Asia/Taipei': 'taiwan', 'Asia/Singapore': 'singapore',
  'Asia/Jakarta': 'indonesia', 'Asia/Pontianak': 'indonesia', 'Asia/Makassar': 'indonesia',
  'Asia/Jayapura': 'indonesia',
  'Asia/Bangkok': 'thailand', 'Asia/Ho_Chi_Minh': 'vietnam', 'Asia/Saigon': 'vietnam',
  'Asia/Manila': 'philippines', 'Asia/Kuala_Lumpur': 'malaysia', 'Asia/Kuching': 'malaysia',
  'Asia/Yangon': 'myanmar', 'Asia/Rangoon': 'myanmar', 'Asia/Phnom_Penh': 'cambodia',
  'Asia/Vientiane': 'laos', 'Asia/Brunei': 'brunei', 'Asia/Dili': 'east timor',
  'Asia/Kolkata': 'india', 'Asia/Calcutta': 'india', 'Asia/Karachi': 'pakistan',
  'Asia/Dhaka': 'bangladesh', 'Asia/Kathmandu': 'nepal', 'Asia/Colombo': 'sri lanka',
  'Asia/Thimphu': 'bhutan', 'Asia/Kabul': 'afghanistan', 'Indian/Maldives': 'maldives',
  'Asia/Tehran': 'iran', 'Asia/Baghdad': 'iraq', 'Asia/Riyadh': 'saudi arabia',
  'Asia/Dubai': 'united arab emirates', 'Asia/Qatar': 'qatar', 'Asia/Kuwait': 'kuwait',
  'Asia/Bahrain': 'bahrain', 'Asia/Muscat': 'oman', 'Asia/Aden': 'yemen',
  'Asia/Amman': 'jordan', 'Asia/Beirut': 'lebanon', 'Asia/Damascus': 'syria',
  'Asia/Jerusalem': 'israel', 'Asia/Tel_Aviv': 'israel', 'Asia/Gaza': 'palestine',
  'Asia/Hebron': 'palestine',
  'Asia/Yerevan': 'armenia', 'Asia/Baku': 'azerbaijan', 'Asia/Tbilisi': 'georgia',
  'Asia/Almaty': 'kazakhstan', 'Asia/Aqtobe': 'kazakhstan', 'Asia/Aqtau': 'kazakhstan',
  'Asia/Atyrau': 'kazakhstan', 'Asia/Oral': 'kazakhstan', 'Asia/Qostanay': 'kazakhstan',
  'Asia/Qyzylorda': 'kazakhstan',
  'Asia/Tashkent': 'uzbekistan', 'Asia/Samarkand': 'uzbekistan', 'Asia/Ashgabat': 'turkmenistan',
  'Asia/Dushanbe': 'tajikistan', 'Asia/Bishkek': 'kyrgyzstan', 'Asia/Ulaanbaatar': 'mongolia',
  'Asia/Yekaterinburg': 'russia', 'Asia/Omsk': 'russia', 'Asia/Novosibirsk': 'russia',
  'Asia/Barnaul': 'russia', 'Asia/Tomsk': 'russia', 'Asia/Novokuznetsk': 'russia',
  'Asia/Krasnoyarsk': 'russia', 'Asia/Irkutsk': 'russia', 'Asia/Chita': 'russia',
  'Asia/Yakutsk': 'russia', 'Asia/Khandyga': 'russia', 'Asia/Vladivostok': 'russia',
  'Asia/Ust-Nera': 'russia', 'Asia/Magadan': 'russia', 'Asia/Sakhalin': 'russia',
  'Asia/Srednekolymsk': 'russia', 'Asia/Kamchatka': 'russia', 'Asia/Anadyr': 'russia',
  // Africa
  'Africa/Cairo': 'egypt', 'Africa/Casablanca': 'morocco', 'Africa/El_Aaiun': 'western sahara',
  'Africa/Algiers': 'algeria', 'Africa/Tunis': 'tunisia', 'Africa/Tripoli': 'libya',
  'Africa/Khartoum': 'sudan', 'Africa/Juba': 'south sudan', 'Africa/Addis_Ababa': 'ethiopia',
  'Africa/Asmara': 'eritrea', 'Africa/Djibouti': 'djibouti', 'Africa/Mogadishu': 'somalia',
  'Africa/Nairobi': 'kenya', 'Africa/Kampala': 'uganda', 'Africa/Kigali': 'rwanda',
  'Africa/Bujumbura': 'burundi', 'Africa/Dar_es_Salaam': 'tanzania',
  'Africa/Lusaka': 'zambia', 'Africa/Harare': 'zimbabwe', 'Africa/Maputo': 'mozambique',
  'Africa/Blantyre': 'malawi', 'Africa/Luanda': 'angola', 'Africa/Windhoek': 'namibia',
  'Africa/Gaborone': 'botswana', 'Africa/Maseru': 'lesotho', 'Africa/Mbabane': 'eswatini',
  'Africa/Johannesburg': 'south africa', 'Indian/Antananarivo': 'madagascar',
  'Africa/Antananarivo': 'madagascar', 'Indian/Mauritius': 'mauritius',
  'Africa/Lagos': 'nigeria', 'Africa/Accra': 'ghana', 'Africa/Abidjan': 'ivory coast',
  'Africa/Dakar': 'senegal', 'Africa/Bamako': 'mali', 'Africa/Ouagadougou': 'burkina faso',
  'Africa/Niamey': 'niger', 'Africa/Ndjamena': 'chad', 'Africa/Douala': 'cameroon',
  'Africa/Libreville': 'gabon', 'Africa/Malabo': 'equatorial guinea',
  'Africa/Bangui': 'central african republic', 'Africa/Sao_Tome': 'sao tome and principe',
  'Africa/Kinshasa': 'democratic republic of the congo',
  'Africa/Lubumbashi': 'democratic republic of the congo',
  'Africa/Brazzaville': 'republic of the congo',
  'Africa/Conakry': 'guinea', 'Africa/Bissau': 'guinea-bissau', 'Africa/Freetown': 'sierra leone',
  'Africa/Monrovia': 'liberia', 'Africa/Lome': 'togo', 'Africa/Porto-Novo': 'benin',
  'Africa/Banjul': 'gambia', 'Africa/Nouakchott': 'mauritania',
  // Oceania
  'Australia/Sydney': 'australia', 'Australia/Melbourne': 'australia',
  'Australia/Brisbane': 'australia', 'Australia/Perth': 'australia',
  'Australia/Adelaide': 'australia', 'Australia/Hobart': 'australia',
  'Australia/Darwin': 'australia', 'Australia/Canberra': 'australia',
  'Australia/Broken_Hill': 'australia', 'Australia/Lindeman': 'australia',
  'Australia/Lord_Howe': 'australia', 'Australia/Eucla': 'australia',
  'Pacific/Auckland': 'new zealand', 'Pacific/Chatham': 'new zealand',
  'Pacific/Fiji': 'fiji', 'Pacific/Port_Moresby': 'papua new guinea', 'Pacific/Guam': 'guam',
  'Pacific/Noumea': 'new caledonia', 'Pacific/Tahiti': 'french polynesia',
  'Pacific/Apia': 'samoa', 'Pacific/Tongatapu': 'tonga', 'Pacific/Efate': 'vanuatu',
  'Pacific/Guadalcanal': 'solomon islands'
}

// Region subtag → country name, for the language fallback. Only the markets that matter to search
// localization (everything ai-srv's SearchRegion has a locale for, plus the obvious neighbours).
const COUNTRY_BY_REGION = {
  TR: 'turkey', DE: 'germany', AT: 'austria', CH: 'switzerland', FR: 'france', BE: 'belgium',
  NL: 'netherlands', LU: 'luxembourg', ES: 'spain', PT: 'portugal', IT: 'italy', GR: 'greece',
  PL: 'poland', CZ: 'czechia', SK: 'slovakia', HU: 'hungary', RO: 'romania', BG: 'bulgaria',
  HR: 'croatia', SI: 'slovenia', RS: 'serbia', UA: 'ukraine', RU: 'russia', BY: 'belarus',
  SE: 'sweden', NO: 'norway', DK: 'denmark', FI: 'finland', IS: 'iceland', IE: 'ireland',
  EE: 'estonia', LV: 'latvia', LT: 'lithuania', CY: 'cyprus', MT: 'malta',
  GB: 'united kingdom', US: 'united states', CA: 'canada', MX: 'mexico', BR: 'brazil',
  AR: 'argentina', CL: 'chile', CO: 'colombia', PE: 'peru', VE: 'venezuela', UY: 'uruguay',
  AU: 'australia', NZ: 'new zealand', IN: 'india', PK: 'pakistan', BD: 'bangladesh',
  LK: 'sri lanka', NP: 'nepal', JP: 'japan', CN: 'china', TW: 'taiwan', HK: 'hong kong',
  KR: 'south korea', ID: 'indonesia', TH: 'thailand', VN: 'vietnam', PH: 'philippines',
  MY: 'malaysia', SG: 'singapore', SA: 'saudi arabia', AE: 'united arab emirates',
  QA: 'qatar', KW: 'kuwait', BH: 'bahrain', OM: 'oman', JO: 'jordan', LB: 'lebanon',
  IL: 'israel', IR: 'iran', IQ: 'iraq', EG: 'egypt', MA: 'morocco', DZ: 'algeria',
  TN: 'tunisia', NG: 'nigeria', GH: 'ghana', KE: 'kenya', ZA: 'south africa',
  AZ: 'azerbaijan', GE: 'georgia', AM: 'armenia', KZ: 'kazakhstan', UZ: 'uzbekistan'
}

// The device's IANA zone, or '' when the environment doesn't expose one.
function currentZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  } catch {
    return ''
  }
}

// The browser's preferred languages, most-preferred first.
function currentLanguages() {
  const nav = typeof navigator === 'undefined' ? null : navigator
  if (!nav) return []
  if (Array.isArray(nav.languages) && nav.languages.length) return nav.languages
  return nav.language ? [nav.language] : []
}

// The first REGION subtag in a list of language tags ("tr-TR" → "TR", "en" → nothing). Script
// subtags are skipped, so "zh-Hant-TW" gives "TW".
function firstRegion(languages) {
  for (const tag of languages || []) {
    for (const part of String(tag || '').split('-').slice(1)) {
      if (/^[A-Za-z]{2}$/.test(part)) return part.toUpperCase()
    }
  }
  return null
}

/**
 * The country the browser gives away for free — no network call, no permission prompt, no await.
 *
 * The TIME ZONE decides. It is the clock the machine is set to, which is where the person is; the
 * UI language is a preference that follows them around ("en-US" is the default on countless devices
 * that have never been near the US), so it is only a FALLBACK for a zone we don't know, and even
 * then English tags are ignored — they are the global default and evidence of nothing. An unknown
 * zone with an English UI therefore returns country: null, because a wrong country (euro prices for
 * an Istanbul shopper, the bug this fixes) is worse than no country.
 *
 * Shape matches reverseGeocode/ipLookup: a lowercase English country NAME, no location, no coords.
 * Overridable arguments exist for tests; production calls it with none.
 */
export function instantGuess({ timeZone = currentZone(), languages = currentLanguages() } = {}) {
  const byZone = COUNTRY_BY_ZONE[String(timeZone || '').trim()] || null
  let country = byZone
  if (!country) {
    const tags = (languages || []).filter((t) => !/^en\b|^en-/i.test(String(t || '')))
    country = COUNTRY_BY_REGION[firstRegion(tags)] || null
  }
  return { location: null, country, lat: null, lng: null }
}

export const state = () => ({
  resolved: false, // true once we've attempted resolution (success or not)
  location: null, // e.g. "Berlin, Germany"
  country: null, // ISO country name lowercase, e.g. "germany" (for search bias)
  lat: null,
  lng: null
})

export const getters = {
  location: (s) => s.location,
  country: (s) => s.country,
  // Compact payload merged into the web-ask request.
  askPayload: (s) => ({
    location: s.location || undefined,
    country: s.country || undefined,
    lat: s.lat ?? undefined,
    lng: s.lng ?? undefined
  })
}

export const mutations = {
  SET(s, geo) {
    s.location = geo.location || null
    s.country = geo.country || null
    s.lat = geo.lat ?? null
    s.lng = geo.lng ?? null
    s.resolved = true
  },
  // A zone/language guess: usable immediately, but NOT a resolution — the chain keeps running and
  // overwrites it with the real answer (and only that real answer is cached for a day).
  GUESS(s, geo) {
    s.location = geo.location || null
    s.country = geo.country || null
    s.lat = geo.lat ?? null
    s.lng = geo.lng ?? null
  },
  RESOLVED(s) {
    s.resolved = true
  }
}

// Precise device position via the browser (city accuracy is all we need, so a cached/coarse fix
// is fine). Resolves null on denial/timeout/unsupported — never throws.
function browserPosition() {
  return new Promise((resolve) => {
    if (!('geolocation' in navigator)) return resolve(null)
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve(pos && pos.coords ? { lat: pos.coords.latitude, lng: pos.coords.longitude } : null),
      () => resolve(null),
      { enableHighAccuracy: false, timeout: 6000, maximumAge: 10 * 60 * 1000 }
    )
  })
}

// Coordinates → "City, Country" (BigDataCloud's free client-side endpoint — no key, CORS-enabled).
async function reverseGeocode(lat, lng) {
  try {
    const resp = await fetch(
      `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`,
      { cache: 'no-store' }
    )
    const d = await resp.json()
    const city = d.city || d.locality || d.principalSubdivision || ''
    const country = d.countryName || ''
    if (!city && !country) return null
    return {
      location: [city, country].filter(Boolean).join(', '),
      country: country ? String(country).toLowerCase() : null,
      lat,
      lng
    }
  } catch {
    return null
  }
}

// Approximate IP-based lookup — two independent providers, first good answer wins.
async function ipLookup() {
  try {
    const resp = await fetch('https://ipwho.is/', { cache: 'no-store' })
    const d = await resp.json()
    if (d && d.success !== false && (d.city || d.country)) {
      return {
        location: [d.city || d.region || '', d.country || ''].filter(Boolean).join(', ') || null,
        country: d.country ? String(d.country).toLowerCase() : null,
        lat: typeof d.latitude === 'number' ? d.latitude : null,
        lng: typeof d.longitude === 'number' ? d.longitude : null
      }
    }
  } catch { /* try the next provider */ }
  try {
    const resp = await fetch('https://ipapi.co/json/', { cache: 'no-store' })
    const d = await resp.json()
    if (d && (d.city || d.country_name)) {
      return {
        location: [d.city || d.region || '', d.country_name || ''].filter(Boolean).join(', ') || null,
        country: d.country_name ? String(d.country_name).toLowerCase() : null,
        lat: typeof d.latitude === 'number' ? d.latitude : null,
        lng: typeof d.longitude === 'number' ? d.longitude : null
      }
    }
  } catch { /* offline or blocked */ }
  return null
}

// One resolution chain at a time: the app warms geo at boot and the first search asks for it again a
// moment later, and running the chain twice means two permission prompts and two IP lookups.
let inFlight = null

// The resolution chain: cache → instant guess → device position → IP. Never throws.
async function runChain({ commit }) {
  const cached = readCache()
  if (cached) {
    commit('SET', cached)
    return
  }
  // 0) Instant: the country implied by the device's time zone. Committed before we await anything
  // so a search fired in the next millisecond still carries a country; it is deliberately NOT
  // cached — it's a guess, and the steps below are expected to replace it within a second or two.
  const guess = instantGuess()
  if (guess.country) commit('GUESS', guess)
  // 1) Precise: the device's own position, reverse-geocoded to "City, Country".
  const pos = await browserPosition()
  if (pos) {
    const geo = await reverseGeocode(pos.lat, pos.lng)
    if (geo) {
      commit('SET', geo)
      writeCache(geo)
      return
    }
  }
  // 2) Approximate: IP lookup.
  const ipGeo = await ipLookup()
  if (ipGeo) {
    commit('SET', ipGeo)
    writeCache(ipGeo)
    return
  }
  // Nothing worked (permission denied AND the IP providers blocked). Mark it done but KEEP the
  // guess — a time-zone country beats no country, and this is exactly the case that used to leave
  // search un-localized forever.
  commit('RESOLVED')
}

export const actions = {
  // Resolve location once (cached). Safe to call repeatedly — it no-ops after the first, and while
  // the first is still running every caller awaits that same run.
  ensure(ctx) {
    if (ctx.state.resolved) return Promise.resolve()
    if (!inFlight) inFlight = runChain(ctx).finally(() => { inFlight = null })
    return inFlight
  }
}
